import { motion } from "framer-motion";
import { SectionLabel, Reveal, KineticLines } from "../Reveal";

const PRINCIPLES = [
  { n: "01", title: "We Listen First", copy: "Understanding your business comes before recommending resources." },
  { n: "02", title: "We Connect You With Specialists", copy: "Find relevant professionals with experience in the areas you want to explore." },
  { n: "03", title: "We Respect Your Relationships", copy: "We're not here to replace the professionals you already trust." },
  { n: "04", title: "You Stay in Control", copy: "No pressure. No unnecessary changes. Your business, your decision." },
];

export default function TrustSection() {
  return (
    <section className="relative bg-navy-900 text-white overflow-hidden grain" data-testid="trust-section">
      <div className="absolute inset-0 bg-arch-dark" aria-hidden="true" />
      <svg className="absolute inset-0 w-full h-full opacity-[0.35]" viewBox="0 0 1440 600" fill="none" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
        <motion.path
          d="M-100 480 C 300 380, 500 560, 820 420 C 1100 290, 1300 420, 1560 300"
          stroke="#15B8A6" strokeWidth="1.4" strokeDasharray="5 9" className="animate-dashFlow" opacity="0.7"
        />
        <motion.path
          d="M-100 340 C 260 260, 560 440, 900 300 C 1180 190, 1320 300, 1560 180"
          stroke="#3D6BA8" strokeWidth="1.2" strokeDasharray="4 10" className="animate-dashFlow" style={{ animationDelay: "0.6s" }} opacity="0.55"
        />
        <motion.path
          d="M-100 200 C 320 140, 620 300, 940 190 C 1200 100, 1360 190, 1560 90"
          stroke="#8FB3D9" strokeWidth="1" strokeDasharray="3 11" className="animate-dashFlow" style={{ animationDelay: "1.1s" }} opacity="0.4"
        />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 md:py-28">
        <div className="max-w-4xl">
          <SectionLabel dark>Why Businesses Talk To Us</SectionLabel>
          <KineticLines
            as="h2"
            className="mt-5 font-display font-extrabold tracking-tightest text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1]"
            lines={["We're Not Here to Sell You Everything.", <>We're Here to Help You Find <span className="text-teal-bright">What Matters.</span></>]}
          />
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-ice/75 max-w-2xl">
              Every business is different. We believe in listening first, understanding your priorities, and
              connecting you with the resources that may genuinely help.
            </p>
          </Reveal>
        </div>

        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 border-t border-l border-white/10">
          {PRINCIPLES.map((p, i) => (
            <Reveal key={p.n} delay={i * 0.07} className="border-r border-b border-white/10">
              <div className="group h-full p-8 md:p-10 transition-colors duration-300 hover:bg-white/[0.04]" data-testid={`trust-principle-${i + 1}`}>
                <span className="text-xs font-extrabold tracking-wider2 text-teal-bright">{p.n}</span>
                <h3 className="mt-4 font-display font-extrabold tracking-tight text-xl md:text-2xl text-white group-hover:text-teal-bright transition-colors">
                  {p.title}
                </h3>
                <span className="block h-[2px] w-10 bg-teal mt-4 transition-all duration-300 group-hover:w-16" aria-hidden="true" />
                <p className="mt-4 text-sm md:text-[15px] leading-relaxed text-ice/65">{p.copy}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
